import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const Counter = ({ value, prefix = "", suffix = "" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.4 });
    const [display, setDisplay] = useState("0");

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, value, {
            duration: 1.8,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setDisplay(String(Math.round(v))),
        });
        return () => controls.stop();
    }, [inView, value]);

    return (
        <span ref={ref} className="font-display text-4xl font-extrabold text-white sm:text-5xl" data-testid="stat-counter">
            {prefix}
            {display}
            {suffix}
        </span>
    );
};

const STATS = [
    { value: 100, suffix: "%", label: "Sybil attack mitigation", sub: "ZK-proof human tethering", accent: "cyan" },
    { value: 12, prefix: "<", suffix: "ms", label: "Verification latency", sub: "Ultra-fast edge proof checks", accent: "emerald" },
    { value: 6, suffix: "+", label: "Tier-1 ecosystems live", sub: "Caldera, Somnia, Union & more", accent: "cyan" },
    { static: "SOC2 / MiCA", label: "Regulatory readiness", sub: "Institutional grade logs", accent: "emerald" },
];

export default function Stats() {
    return (
        <section id="network" className="scroll-mt-24 px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
            <div className="mx-auto max-w-7xl">
                <Reveal>
                    <h2 className="max-w-2xl font-display text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl">
                        Proof of scale.
                    </h2>
                </Reveal>
                <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
                    {STATS.map((s, i) => (
                        <Reveal key={s.label} delay={i * 0.08}>
                            <div
                                data-testid={`stat-card-${i}`}
                                className={`h-full rounded-2xl border border-white/10 bg-[rgba(11,15,25,0.6)] p-7 transition-colors duration-300 hover:border-white/20 ${
                                    s.accent === "cyan" ? "border-t-2 border-t-neon-cyan/60" : "border-t-2 border-t-neon-emerald/60"
                                }`}
                            >
                                {s.static ? (
                                    <span className="font-display text-3xl font-extrabold text-white sm:text-4xl">
                                        {s.static}
                                    </span>
                                ) : (
                                    <Counter value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />
                                )}
                                <p className="mt-4 text-sm font-semibold text-slate-200">{s.label}</p>
                                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
                                    {s.sub}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}